import React from 'react';
import { Paper, Typography, Box } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { DailyKPI } from '../types';
import { colors, shadows } from '../theme/designSystem';

interface EfficiencyTrendChartProps {
  data: DailyKPI[];
  targetMin?: number;
  targetMax?: number;
}

const EfficiencyTrendChart: React.FC<EfficiencyTrendChartProps> = ({ 
  data, 
  targetMin = 0.3, 
  targetMax = 0.6 
}) => {
  const chartData = data.map((item) => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    gasYield: parseFloat(Number(item.kpi_gas_yield_m3_per_kg).toFixed(4)),
  })).reverse();

  return (
    <Paper 
      sx={{ 
        p: '24px',
        height: '100%',
        minHeight: '400px',
        borderRadius: '12px',
        border: `1px solid ${colors.neutral[200]}`,
        boxShadow: shadows.card,
      }}
    >
      <Box sx={{ mb: '20px' }}>
        <Typography 
          sx={{ 
            fontSize: '1.25rem',
            fontWeight: 600,
            color: colors.neutral[900],
            mb: 0.5,
          }}
        >
          Gas Yield Efficiency Trend
        </Typography>
        <Typography 
          sx={{ 
            fontSize: '0.875rem',
            color: colors.neutral[500],
          }}
        >
          Last 30 Days Performance
        </Typography>
      </Box>
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[100]} />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12, fill: colors.neutral[500] }}
          />
          <YAxis 
            label={{ value: 'm³/kg', angle: -90, position: 'insideLeft', style: { fill: colors.neutral[500], fontSize: 12 } }}
            domain={[0, 1]}
            tick={{ fontSize: 12, fill: colors.neutral[500] }}
          />
          <Tooltip />
          <Legend />
          <ReferenceLine 
            y={targetMin} 
            stroke="red" 
            strokeDasharray="3 3" 
            label="Min Target" 
          />
          <ReferenceLine 
            y={targetMax} 
            stroke="green" 
            strokeDasharray="3 3" 
            label="Optimal" 
          />
          <Line 
            type="monotone" 
            dataKey="gasYield" 
            stroke={colors.primary[600]} 
            strokeWidth={3}
            name="Gas Yield (m³/kg)"
            dot={{ r: 4, fill: colors.primary[600] }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default EfficiencyTrendChart;
